import { useState, useRef, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';

const MAX_RECORDING_SECONDS = 120;

// Negotiate the best supported MIME type for audio recording
function getSupportedMimeType(): string {
	const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
	for (const mime of candidates) {
		if (MediaRecorder.isTypeSupported(mime)) {
			return mime;
		}
	}
	return '';
}

export interface UseVoiceInputReturn {
	isRecording: boolean;
	isTranscribing: boolean;
	error: string | null;
	startRecording: () => Promise<void>;
	stopRecording: () => void;
	isSupported: boolean;
}

interface UseVoiceInputOptions {
	containerAppUrl?: string | null;
	onTranscript: (text: string) => void;
}

export function useVoiceInput({ containerAppUrl, onTranscript }: UseVoiceInputOptions): UseVoiceInputReturn {
	const [isRecording, setIsRecording] = useState(false);
	const [isTranscribing, setIsTranscribing] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const mediaRecorderRef = useRef<MediaRecorder | null>(null);
	const streamRef = useRef<MediaStream | null>(null);
	const chunksRef = useRef<Blob[]>([]);
	const autoStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const mimeTypeRef = useRef<string>('');

	const isSupported =
		typeof navigator !== 'undefined' &&
		!!navigator.mediaDevices?.getUserMedia &&
		typeof MediaRecorder !== 'undefined';

	const stopTracks = useCallback(() => {
		if (streamRef.current) {
			streamRef.current.getTracks().forEach((track) => track.stop());
			streamRef.current = null;
		}
		if (autoStopTimerRef.current !== null) {
			clearTimeout(autoStopTimerRef.current);
			autoStopTimerRef.current = null;
		}
	}, []);

	const handleStop = useCallback(async () => {
		const chunks = chunksRef.current;
		const mimeType = mimeTypeRef.current;
		chunksRef.current = [];

		setIsRecording(false);
		stopTracks();

		if (chunks.length === 0) {
			return;
		}

		if (!containerAppUrl) {
			setError('No workspace connected. Cannot transcribe audio.');
			return;
		}

		const audioBlob = new Blob(chunks, { type: mimeType || 'audio/webm' });

		setIsTranscribing(true);
		setError(null);
		try {
			const text = await apiService.transcribeAudio(audioBlob, mimeType, containerAppUrl);
			onTranscript(text);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Transcription failed. Please try again.');
		} finally {
			setIsTranscribing(false);
		}
	}, [containerAppUrl, onTranscript, stopTracks]);

	const startRecording = useCallback(async () => {
		if (!isSupported) return;

		setError(null);
		chunksRef.current = [];

		let stream: MediaStream;
		try {
			stream = await navigator.mediaDevices.getUserMedia({ audio: true });
		} catch (err) {
			if (err instanceof DOMException) {
				if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
					setError('Microphone access denied. Please allow microphone access and try again.');
				} else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
					setError('No microphone found. Please connect a microphone and try again.');
				} else {
					setError('Could not access microphone. Please check your device settings.');
				}
			} else {
				setError('Could not access microphone. Please check your device settings.');
			}
			return;
		}

		streamRef.current = stream;
		const mimeType = getSupportedMimeType();
		mimeTypeRef.current = mimeType;

		const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
		mediaRecorderRef.current = recorder;

		recorder.ondataavailable = (e) => {
			if (e.data.size > 0) {
				chunksRef.current.push(e.data);
			}
		};

		recorder.onstop = () => {
			handleStop();
		};

		recorder.start();
		setIsRecording(true);

		// Auto-stop after max duration
		autoStopTimerRef.current = setTimeout(() => {
			if (mediaRecorderRef.current?.state === 'recording') {
				mediaRecorderRef.current.stop();
			}
		}, MAX_RECORDING_SECONDS * 1000);
	}, [isSupported, handleStop]);

	const stopRecording = useCallback(() => {
		if (mediaRecorderRef.current?.state === 'recording') {
			mediaRecorderRef.current.stop();
			// onstop fires handleStop asynchronously
		}
	}, []);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (mediaRecorderRef.current?.state === 'recording') {
				mediaRecorderRef.current.stop();
			}
			stopTracks();
		};
	}, [stopTracks]);

	return { isRecording, isTranscribing, error, startRecording, stopRecording, isSupported };
}
